import { create } from 'zustand'
import { ACCESS_TOKEN, getCookie, setCookie, removeCookie } from '@/lib/cookies'

export type Role = string
export type Permission = string

export interface AuthUser {
  id: string
  accountNo?: string
  email: string
  phone?: string | null
  username: string
  avatarUrl?: string | null
  coverUrl?: string | null
  type: 'STAFF' | 'USER' | 'REGULAR'
  isSuperAdmin: boolean
  roles: Role[]
  permissions: Permission[]
  exp: number
}

interface AuthState {
  auth: {
    user: AuthUser | null
    accessToken: string
    setUser: (user: AuthUser | null) => void
    setAccessToken: (accessToken: string) => void
    resetAccessToken: () => void
    reset: () => void
    isAuthenticated: () => boolean
    login: (email: string, password: string) => Promise<boolean>
    logout: () => void
    refreshToken: () => Promise<boolean>
    hasRole: (role: Role | Role[]) => boolean
    hasPermission: (permission: Permission | Permission[]) => boolean
    isSuperAdmin: () => boolean
    canAccess: (perms?: Permission[], roles?: Role[]) => boolean
  }
}

const cookieState = getCookie(ACCESS_TOKEN)
const initToken = cookieState && cookieState !== 'undefined' ? cookieState : ''

const initUser: AuthUser | null = (() => {
  if (typeof window === 'undefined') return null
  try {
    const stored = sessionStorage.getItem('auth_user')
    return stored ? (JSON.parse(stored) as AuthUser) : null
  } catch {
    return null
  }
})()

const hasAny = (list: string[], target: string | string[]) =>
  (Array.isArray(target) ? target : [target]).some((x) => list.includes(x))

export const useAuthStore = create<AuthState>()((set, get) => {
  const clearAuth = () =>
    set((s) => {
      removeCookie(ACCESS_TOKEN)
      if (typeof window !== 'undefined') sessionStorage.removeItem('auth_user')
      return { ...s, auth: { ...s.auth, user: null, accessToken: '' } }
    })

  return {
    auth: {
      user: initUser,
      accessToken: initToken,
      setUser: (user) =>
        set((s) => {
          if (typeof window !== 'undefined') {
            if (user) sessionStorage.setItem('auth_user', JSON.stringify(user))
            else sessionStorage.removeItem('auth_user')
          }
          return { ...s, auth: { ...s.auth, user } }
        }),
      setAccessToken: (accessToken) =>
        set((s) => {
          setCookie(ACCESS_TOKEN, accessToken, {
            maxAge: 60 * 60 * 24 * 7,
            secure: import.meta.env.PROD,
            sameSite: 'strict',
          })
          return { ...s, auth: { ...s.auth, accessToken } }
        }),
      resetAccessToken: () =>
        set((s) => {
          removeCookie(ACCESS_TOKEN)
          return { ...s, auth: { ...s.auth, accessToken: '' } }
        }),
      reset: clearAuth,
      logout: clearAuth,
      isAuthenticated: () => {
        const { accessToken, user } = get().auth
        return Boolean(
          accessToken && user && (!user.exp || Date.now() <= user.exp)
        )
      },
      hasRole: (role) => {
        const u = get().auth.user
        return Boolean(u && (u.isSuperAdmin || hasAny(u.roles, role)))
      },
      hasPermission: (perm) => {
        const u = get().auth.user
        return Boolean(u && (u.isSuperAdmin || hasAny(u.permissions, perm)))
      },
      isSuperAdmin: () => Boolean(get().auth.user?.isSuperAdmin),
      canAccess: (perms, roles) => {
        const { auth } = get()
        if (!auth.user) return false
        if (auth.isSuperAdmin()) return true
        if (perms?.length && auth.hasPermission(perms)) return true
        if (roles?.length && auth.hasRole(roles)) return true
        return !perms?.length && !roles?.length
      },
      login: async () => false,
      refreshToken: async () => false,
    },
  }
})
